// FILE: src/components/case-studies/admin/ValidationPanel.js
// Runs the server-side publish gate (validate_case_study, migration 071) and
// layers client-side ADVISORY warnings on top (unknown thematic tags,
// registry pre-checks). Blocking errors and advisories are visually and
// semantically distinct: red ✕ blocks publishing, amber ⚠ never does.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { validateCaseStudy } from '../../../services/caseStudyService';
import { getQuestionType, isSupportedQuestionType } from '../../../questionTypes';
import { tagWarnings } from './caseAdminUtils';
import { S, T } from './caseAdminStyles';

export default function ValidationPanel({ caseStudyId, full, publishResult }) {
  const [server, setServer] = useState(null);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try { setError(null); setServer(await validateCaseStudy(caseStudyId)); }
    catch (e) { setError(e.message); }
  }, [caseStudyId]);
  useEffect(() => { refresh(); }, [refresh, full]);

  // Client-side advisories (never blocking).
  const advisories = useMemo(() => {
    const out = tagWarnings(full.case.tags);
    (full.questions || []).forEach((q) => {
      if (!isSupportedQuestionType(q.type)) return; // server already blocks these
      const def = getQuestionType(q.type);
      const issues = def ? def.validate({
        type: q.type, choices: q.choices, correctAnswers: q.correct_answers, image: q.image,
      }) : [];
      issues.forEach((msg) => out.push(`Q${q.case_sequence} (${q.id}): ${msg} Fix it in the Question Editor.`));
    });
    if ((full.case.summary || '').trim() === '') out.push('The case summary is empty — learners see it on the intro screen.');
    return out;
  }, [full]);

  const errors = (server && server.errors) || [];
  const published = publishResult && publishResult.status === 'published';

  return (
    <div style={{ maxWidth: 860 }} data-testid="validation-panel">
      {error && <div style={S.err}>{error}</div>}
      {published && (
        <div style={{ background: T.goodSoft, border: `1px solid ${T.good}`, color: T.good, borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontWeight: 700 }}
          data-testid="publish-success">
          ✓ Published as v{publishResult.version}
        </div>
      )}

      <div style={{ ...S.panel, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
          <h2 style={{ ...S.h1, margin: 0 }}>Blocking errors</h2>
          <span style={{ ...S.chip(errors.length ? T.badSoft : T.goodSoft, errors.length ? T.bad : T.good), marginLeft: 10 }}
            data-testid="error-count">
            {server === null ? '…' : errors.length === 0 ? 'None — publishable' : `${errors.length} blocking`}
          </span>
          <span style={{ flex: 1 }} />
          <button style={S.btnGhost} onClick={refresh} data-testid="validation-refresh">Re-run</button>
        </div>
        {errors.length === 0 && server !== null && (
          <div style={S.sub}>The publish gate passes. Publishing will {full.case.published_at ? 'bump the version and ' : ''}make this case visible to learners once the learner flow ships.</div>
        )}
        {errors.map((e, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, padding: '7px 10px', background: T.badSoft, borderRadius: 8, marginBottom: 6, fontSize: '0.84rem' }}
            data-testid={`validation-error-${e.code}`}>
            <span style={{ color: T.bad, fontWeight: 800 }}>✕</span>
            <span><strong style={{ color: T.bad }}>{e.code}</strong> — {e.message}</span>
          </div>
        ))}
      </div>

      <div style={S.panel}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
          <h2 style={{ ...S.h1, margin: 0 }}>Advisories</h2>
          <span style={{ ...S.chip(T.warnSoft, '#8a6116'), marginLeft: 10 }} data-testid="advisory-count">
            {advisories.length === 0 ? 'None' : `${advisories.length} advisory — publishing is NOT blocked`}
          </span>
        </div>
        {advisories.length === 0 && <div style={S.sub}>No advisories.</div>}
        {advisories.map((w, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, padding: '7px 10px', background: T.warnSoft, borderRadius: 8, marginBottom: 6, fontSize: '0.84rem' }}
            data-testid="validation-advisory">
            <span style={{ color: '#8a6116', fontWeight: 800 }}>⚠</span>
            <span style={{ color: '#6b4d12' }}>{w}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
